import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import "../styles/Navbar.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faXmark } from "@fortawesome/free-solid-svg-icons";

const LEFT = ["About", "Services", "Reviews"];
const RIGHT = ["FAQ", "Contact"];
const MOBILE = [...LEFT, ...RIGHT];

const toId = (label) => label.toLowerCase().replace(/\s+/g, "");

const Navbar = ({ animate, onComplete }) => {
  const navRef = useRef(null);
  const animatedRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    gsap
      .timeline({
        onComplete: () => {
          setMenuOpen(false);
          setIsClosing(false);
        },
      })
      .to(".mobile-menu-links li", {
        y: 20,
        opacity: 0,
        stagger: 0.2,
        duration: 0.4,
        ease: "power2.inOut",
      })
      .to(
        ".mobile-menu",
        { y: "-100%", duration: 0.7, ease: "power4.out" },
        "-=0.2"
      );
  };

  useEffect(() => {
    if (menuOpen && !isClosing) {
      gsap.set(".mobile-menu", { y: "-100%" });
      gsap.set(".mobile-menu-links li", { y: 20, opacity: 0 });
      gsap
        .timeline()
        .to(".mobile-menu", { y: 0, duration: 0.7, ease: "power4.out" })
        .to(
          ".mobile-menu-links li",
          {
            y: 0,
            opacity: 1,
            stagger: 0.18,
            duration: 0.45,
            ease: "power3.out",
          },
          "-=0.25"
        );
    }
  }, [menuOpen, isClosing]);

  useEffect(() => {
    if (animate && !animatedRef.current) {
      animatedRef.current = true;
      gsap.set(".nav-item", { opacity: 0, y: 20 });
      gsap.timeline({ onComplete }).to(".nav-item", {
        opacity: 1,
        y: 0,
        stagger: 0.2,
        duration: 0.6,
        ease: "power3.out",
      });
    }
  }, [animate, onComplete]);

  const handleSmoothLink = (id) => (e) => {
    e.preventDefault();
    const section = document.querySelector(`#${toId(id)}`);
    if (!section) return;
    // If mobile menu open, close then scroll
    if (menuOpen) {
      handleClose();
      setTimeout(() => section.scrollIntoView({ behavior: "smooth" }), 700);
    } else {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      {/* Desktop Navbar */}
      <nav ref={navRef} className={animate ? "show" : ""}>
        <div className="nav-container">
          <div className="nav-item links-1">
            {LEFT.map((label) => (
              <a
                key={label}
                href={`#${toId(label)}`}
                onClick={handleSmoothLink(label)}
              >
                {label}
              </a>
            ))}
          </div>

          <div className="nav-logo">Auto DrivR</div>

          <div className="nav-item links-2">
            {RIGHT.map((label) => (
              <a
                key={label}
                href={`#${toId(label)}`}
                onClick={handleSmoothLink(label)}
              >
                {label}
              </a>
            ))}
          </div>

          <div
            className="hamburger"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <FontAwesomeIcon icon={faBars} />
          </div>
        </div>
      </nav>

      {/* Mobile Fullscreen Menu */}
      {menuOpen && (
        <div className="mobile-menu" role="dialog" aria-modal="true">
          <div className="mobile-menu-header">
            <span className="mobile-logo">Auto DrivR</span>
            <FontAwesomeIcon
              icon={faXmark}
              className="close-icon"
              onClick={handleClose}
              role="button"
              aria-label="Close menu"
            />
          </div>

          <ul className="mobile-menu-links">
            {MOBILE.map((label) => (
              <li key={label}>
                <a href={`#${toId(label)}`} onClick={handleSmoothLink(label)}>
                  {label}
                </a>
              </li>
            ))}
          </ul>

          <div className="mobile-menu-footer" />
        </div>
      )}
    </>
  );
};

export default Navbar;
